import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  CircularProgress,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Divider,
} from '@mui/material';

import ViewHeader from 'components/rootComponents/Standards/ViewHeader/ViewHeader';
import Chart from 'components/charts/Charts';
import useCharts from 'utils/customHooks/useCharts';
import { IEncuestas } from '../Encuestas.interface';

const getSessionId = () => {
  return localStorage.getItem('sessionGroup') || '';
};

interface IChartDataExtended {
  labels: string[];
  datasets: any[];
  openResponses?: string[];
}

const getSemesterFromName = (name?: string): '1' | '2' | 'anual' => {
  if (!name) return 'anual';
  const lowerName = name.toLowerCase();

  // Si incluye algo como "1s" o "1 semestre" o "2023-1"
  if (/1s|1\s*semestre|202[0-9]-1/.test(lowerName)) return '1';
  if (/2s|2\s*semestre|202[0-9]-2/.test(lowerName)) return '2';

  // Detectar nombres como "tercer semestre", "cuarto semestre", etc. (ahora también décimo)
  const ordinalMatch = lowerName.match(
    /(primer|segundo|tercer|cuarto|quinto|sexto|séptimo|septimo|octavo|noveno|décimo|decimo)\s+semestre/
  );
  if (ordinalMatch) {
    const mapOrdinal: Record<string, number> = {
      primer: 1,
      segundo: 2,
      tercer: 3,
      cuarto: 4,
      quinto: 5,
      sexto: 6,
      séptimo: 7,
      septimo: 7,
      octavo: 8,
      noveno: 9,
      décimo: 10,
      decimo: 10,
    };
    const num = mapOrdinal[ordinalMatch[1]];
    return num % 2 === 1 ? '1' : '2'; // impar = '1', par = '2'
  }

  // Detectar si hay un número explícito: "10 semestre", "11 semestre", etc.
  const numericMatch = lowerName.match(/(\b[1-9]+)\s*semestre/);
  if (numericMatch) {
    const semNumber = parseInt(numericMatch[1], 10);
    return semNumber % 2 === 1 ? '1' : '2';
  }

  // Si no coincide con nada, lo consideramos "anual"
  return 'anual';
};

const groupChartsBySemester = (charts: any[]) => {
  return charts.reduce(
    (acc, chart) => {
      const semester = getSemesterFromName(chart?.name);
      acc[semester].push(chart);
      return acc;
    },
    { '1': [], '2': [], 'anual': [] } as Record<'1' | '2' | 'anual', any[]>
  );
};

const EncuestasGraficasUC = (props: IEncuestas) => {
  const title = 'Encuestas Unidades Curriculares';
  const search = props.location.search;
  const id = new URLSearchParams(search).get('id') || getSessionId();
  const { isLoadingCharts, charts } = useCharts(
    window.location.pathname,
    props.filters,
    id
  );

  // Estado para controlar la apertura del modal y almacenar los comentarios a mostrar
  const [modalOpen, setModalOpen] = useState(false);
  const [modalComments, setModalComments] = useState<string[]>([]);

  // Función que se invoca al hacer clic para mostrar comentarios
  const handleOpenModal = (comments: string[]) => {
    setModalComments(comments);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalComments([]);
  };

  const renderCharts = (chartsGroup: any[]) =>
    chartsGroup.map((chart, index) => {
      const data = chart.data as IChartDataExtended | undefined;
      const openResponses = data?.openResponses || [];

      if (data && data.labels && chart.chartType === 'Table' && data.labels.length > 0) {
        // Se asigna un nombre modificado para evitar repetir el título dentro del Chart
        const chartNoTitle = { ...chart, name: 'Encuesta Unidades Curriculares' };
        return (
          <Grid item key={`${chart.name}-${index}`} xs={12}>
            <div
              style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '16px',
                marginBottom: '16px',
              }}
            >
              {/* Encabezado con el título */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <h2 style={{ margin: 0 }}>{chart.name}</h2>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {/* Ejemplo de botón para abrir comentarios, si hiciera falta:
                  {openResponses.length > 0 && (
                    <Button variant="outlined" onClick={() => handleOpenModal(openResponses)}>
                      Ver Comentarios
                    </Button>
                  )} */}
                </div>
              </div>
              {/* Renderiza el Chart */}
              <Chart {...chartNoTitle} />
            </div>
          </Grid>
        );
      }
      return null;
    });

  const { '1': semestre1, '2': semestre2, anual } = groupChartsBySemester(charts);

  return (
    <Grid container spacing={2} style={{ alignItems: 'center', marginTop: '0px' }}>
      <ViewHeader title={title} chartUrl={'/v1.1/navegacion/encuestasuc/graficas'} />

      {isLoadingCharts && (
        <Grid item xs={12} style={{ textAlign: 'center' }}>
          <CircularProgress />
        </Grid>
      )}

      {!isLoadingCharts && (
        <>
          {/* Mostrar primero las encuestas Anual, si existen */}
          {anual.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">Anual</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider
                  sx={{ borderBottomWidth: 3, borderColor: 'black', mt: 1, mb: 2 }}
                />
              </Grid>
              {renderCharts(anual)}
            </>
          )}

          {/* Luego, mostrar Segundo Semestre */}
          {semestre2.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">Semestre 2</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider
                  sx={{ borderBottomWidth: 3, borderColor: 'black', mt: 1, mb: 2 }}
                />
              </Grid>
              {renderCharts(semestre2)}
            </>
          )}

          {/* Finalmente, mostrar Primer Semestre */}
          {semestre1.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">Semestre 1</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider
                  sx={{ borderBottomWidth: 3, borderColor: 'black', mt: 1, mb: 2 }}
                />
              </Grid>
              {renderCharts(semestre1)}
            </>
          )}
        </>
      )}

      <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogTitle>Comentarios de los Estudiantes</DialogTitle>
        <DialogContent dividers>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>#</TableCell>
                <TableCell>Comentario</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modalComments.map((comentario, idx) => (
                <TableRow
                  key={idx}
                  style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa' }}
                >
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{comentario}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

const mapStateToProps = (state: any) => {
  return { filters: state.filters };
};

export default connect(mapStateToProps)(EncuestasGraficasUC);

